<template>
  <div class="filtro-slider">
    <div class="slider-header mb-3">
      <span>{{ configuracionFiltro.nombre }}</span>
    </div>

    <div class="slider-container">
      <div ref="sliderTrack" class="slider-track">
        <div class="slider-range" :style="rangeStyle"></div>
        <input
          v-model="valorMinimo"
          type="range"
          :min="configuracionFiltro.min"
          :max="configuracionFiltro.max"
          class="slider-input slider-min"
          @input="updateMinimo" />
        <input
          v-model="valorMaximo"
          type="range"
          :min="configuracionFiltro.min"
          :max="configuracionFiltro.max"
          class="slider-input slider-max"
          @input="updateMaximo" />
      </div>
    </div>

    <div class="row mb-20">
      <div class="col-4 col-md-6 pe-2">
        <div class="input-wrapper input-wrapper-min">
          <input
            v-model="valorMinimo"
            type="number"
            :min="configuracionFiltro.min"
            :max="configuracionFiltro.max"
            class="input-valor small text-landing"
            @input="updateMinimo" />
          <span class="currency p text-landing">{{ agenciaStore.getCurrency }}</span>
        </div>
      </div>
      <div class="col-4 col-md-6 offset-4 offset-md-0 ps-2">
        <div class="input-wrapper input-wrapper-max">
          <input
            v-model="valorMaximo"
            type="number"
            :min="configuracionFiltro.min"
            :max="configuracionFiltro.max"
            class="input-valor small text-landing"
            @input="updateMaximo" />
          <span class="currency p text-landing">{{ agenciaStore.getCurrency }}</span>
        </div>
      </div>
    </div>

    <div class="text-end">
      <div class="btn-aplicar p medium pointer text-landing mb-m-10" @click="selectRange">
        {{ $t('general.aplicar') }}
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { agenciaStoreModule } from '@/store_pinia/agencia';

  const agenciaStore = agenciaStoreModule();

  const props = defineProps({
    configuracionFiltro: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(['selected']);

  const valorMinimo = ref();
  const valorMaximo = ref();

  const rangeStyle = computed(() => {
    const min = props.configuracionFiltro.min;
    const max = props.configuracionFiltro.max;
    const leftPercent = ((valorMinimo.value - min) / (max - min)) * 100;
    const rightPercent = ((valorMaximo.value - min) / (max - min)) * 100;

    return {
      left: `${leftPercent}%`,
      width: `${rightPercent - leftPercent}%`,
    };
  });

  function updateMinimo() {
    if (valorMinimo.value > valorMaximo.value) {
      valorMinimo.value = valorMaximo.value;
    }
  }

  function updateMaximo() {
    if (valorMaximo.value < valorMinimo.value) {
      valorMaximo.value = valorMinimo.value;
    }
  }

  function selectRange() {
    emit('selected', {
      id: props.configuracionFiltro.id,
      idFiltro: props.configuracionFiltro.id,
      tituloFiltro: props.configuracionFiltro.titulo,
      valor: {
        min: valorMinimo.value,
        max: valorMaximo.value,
        nombre: `${valorMinimo.value} ${agenciaStore.getCurrency} - ${valorMaximo.value} ${agenciaStore.getCurrency}`,
      },
    });
  }

  onMounted(() => {
    if (props.configuracionFiltro) {
      valorMinimo.value = props.configuracionFiltro.min;
      valorMaximo.value = props.configuracionFiltro.max;
    }
  });
</script>

<style scoped lang="scss">
  .filtro-slider {
    width: 300px;
    padding: 10px;

    @media screen and (max-width: 768px) {
      width: 100%;
    }
  }

  .slider-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .slider-container {
    position: relative;
    margin: 15px 0;
  }

  .slider-track {
    position: relative;
    height: 6px;
    background-color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.2);
    border-radius: 3px;
    margin: 10px 0 30px 0;
  }

  .slider-range {
    position: absolute;
    height: 100%;
    background-color: var(--bs-secondary);
    border-radius: 3px;
    transition: all 0.1s ease;
  }

  .slider-input {
    position: absolute;
    top: -8px;
    width: 100%;
    height: 22px;
    background: none;
    pointer-events: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 24px;
      height: 24px;
      background: var(--bs-white);
      border-radius: 50%;
      cursor: pointer;
      pointer-events: all;
      box-shadow: 4px 6px 20px 0px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1), 2px 4px 5px 0px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1),
        0px 0px 1px 0px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1);
    }

    &::-moz-range-thumb {
      width: 24px;
      height: 24px;
      background: var(--bs-white);
      border-radius: 50%;
      cursor: pointer;
      pointer-events: all;
      border: none;
      box-shadow: 4px 6px 20px 0px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1), 2px 4px 5px 0px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1),
        0px 0px 1px 0px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1);
    }
  }

  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;

    .input-valor {
      width: 100%;
      padding: 10px 40px 10px 10px;
      border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.2);
      border-radius: 4px;
      box-shadow: 0px 0px 3px 0px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1) inset;
      outline: none;
      background-color: var(--landing-background-color, white);
      text-align: start;

      &:focus {
        border-color: rgb(var(--primary-rgb));
      }

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &[type='number'] {
        appearance: textfield;
        -moz-appearance: textfield;
      }
    }

    .currency {
      position: absolute;
      right: 10px;
      pointer-events: none;
      user-select: none;
    }

    &.input-wrapper-min {
      @media screen and (max-width: 768px) {
        text-align: left;
      }
    }

    &.input-wrapper-max {
      @media screen and (max-width: 768px) {
        text-align: right;
      }
    }
  }

  .btn-aplicar {
    padding: 10px 24px;
    border: 1px solid var(--landing-text-color, var(--bs-primary));
    border-radius: var(--bs-border-radius-xxl);
    transition: all 0.2s ease;
    display: inline-block;

    &:hover {
      background-color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1);
    }

    @media screen and (max-width: 768px) {
      width: 100%;
      display: block;
      text-align: center;
    }
  }
</style>
